import { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/* ---------------------------------------------------------------- */
/* Lifecycle badges — the UI never derives status, it only renders it */
/* ---------------------------------------------------------------- */

const LIFECYCLE_LABELS: Record<string, string> = {
  draft: "Draft",
  internal_review: "Internal Review",
  approved: "Approved",
  published: "Published",
  deprecated: "Deprecated",
  superseded: "Superseded",
  archived: "Archived",
  blocked: "Publication Blocked",
  legacy_verified: "Legacy Published — Verified",
  legacy_unverified: "Legacy Published — Unverified",
  planned: "Planned",
  validated: "Validated",
  succeeded: "Succeeded",
  failed: "Failed",
};

const LIFECYCLE_CLASSES: Record<string, string> = {
  draft: "bg-muted text-muted-foreground border-border",
  internal_review: "bg-warning/15 text-warning-foreground border-warning/40",
  approved: "bg-primary/15 text-primary border-primary/40",
  published: "bg-success/15 text-success border-success/40",
  deprecated: "bg-muted text-muted-foreground border-border line-through",
  superseded: "bg-muted text-muted-foreground border-border",
  archived: "bg-muted text-muted-foreground border-border",
  blocked: "bg-destructive/15 text-destructive border-destructive/50",
  legacy_verified: "bg-accent text-accent-foreground border-border",
  legacy_unverified: "bg-warning/15 text-warning-foreground border-warning/40",
  succeeded: "bg-success/15 text-success border-success/40",
  failed: "bg-destructive/15 text-destructive border-destructive/50",
};

export function LifecycleBadge({
  status,
  blocked = false,
}: {
  status?: string | null;
  blocked?: boolean;
}) {
  const key = blocked ? "blocked" : (status ?? "").toLowerCase();
  const label =
    LIFECYCLE_LABELS[key] ??
    (status ? status.replace(/_/g, " ") : "Unknown");
  return (
    <span
      role="status"
      aria-label={`Lifecycle status: ${label}`}
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${
        LIFECYCLE_CLASSES[key] ?? "bg-muted text-muted-foreground border-border"
      }`}
    >
      {label}
    </span>
  );
}

/* ---------------------------------------------------------------- */

export function ImmutabilityNote({ status }: { status?: string | null }) {
  if (status === "published") {
    return (
      <span className="text-xs text-muted-foreground">
        Published methodology — immutable. Correct forward with a new version.
      </span>
    );
  }
  if (status === "internal_review" || status === "draft") {
    return (
      <span className="text-xs text-muted-foreground">
        Not client-facing. This content may not be shown to respondents or clients.
      </span>
    );
  }
  return null;
}

/* ---------------------------------------------------------------- */

export function HashLabel({ value }: { value?: string | null }) {
  if (!value) return <span className="text-muted-foreground italic">None</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <code className="cursor-help rounded bg-muted px-1 py-0.5 font-mono text-xs">
          {value.slice(0, 12)}…
        </code>
      </TooltipTrigger>
      <TooltipContent>
        <span className="break-all font-mono text-xs">{value}</span>
      </TooltipContent>
    </Tooltip>
  );
}

export function DateLabel({ value }: { value?: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <span>{new Date(value).toLocaleDateString("en-US")}</span>;
}

/* ---------------------------------------------------------------- */

export function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div role="status" className="p-6 text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <Alert variant="destructive">
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

/* ---------------------------------------------------------------- */

const REASON_TEXT: Record<string, string> = {
  runtime_disabled: "The Composition Runtime is globally disabled.",
  composition_disabled: "Composition is globally disabled.",
  applicability_disabled: "The applicability engine is globally disabled.",
  blueprint_not_approved: "The Blueprint has not been approved.",
  methodology_unpublished: "One or more methodology dependencies are not published.",
  publication_blocked: "A publication blocker is set on a dependency.",
  dark_test_blueprint: "This is a dark-test plan and can never be approved.",
  readiness_unavailable: "The server readiness check could not be evaluated.",
  eligibility_unavailable: "The server eligibility check could not be evaluated.",
  not_composed: "This assessment was not produced by the composition runtime.",
  legacy_assessment: "Legacy assessment — modular composition was not captured.",
};

export function ReasonCodes({ codes }: { codes?: string[] | null }) {
  if (!codes || codes.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {codes.map((c) => (
        <li key={c} className="text-sm">
          <span>{REASON_TEXT[c] ?? c.replace(/_/g, " ")}</span>{" "}
          <code className="rounded bg-muted px-1 text-xs text-muted-foreground">{c}</code>
        </li>
      ))}
    </ul>
  );
}

export function ReadinessCard({
  title,
  ready,
  codes,
  children,
}: {
  title: string;
  ready: boolean;
  codes?: string[] | null;
  children?: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span>{title}</span>
          <Badge variant={ready ? "default" : "destructive"}>
            {ready ? "Allowed" : "Denied"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!ready && <ReasonCodes codes={codes} />}
        {children}
      </CardContent>
    </Card>
  );
}

export function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{value}</div>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

export function AdvancedJson({ data }: { data: unknown }) {
  return (
    <pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
